import React, { FC, useMemo, useCallback, useEffect, useRef } from 'react';
import { ReactComponent as PlusCircleIcon } from '@blink/assets/src/icons/plusCircle.svg';
import { ButtonStroke } from '@blink/components';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AssetCard } from './AssetCard/AssetCard';
import { SelectedBorrowAsset } from '../../../BorrowModal';
import { useAssetSectionStyle } from './AssetSection.style';
import { RiskFactorFields } from '../../RiskFactor';

import { BorrowModalData, LeverageStrategy } from '@/shared/validations/borrowModal.schema';
import { Agreement, Token, UserConversionRates } from '@/shared/api/borrow';
import { Store } from '@/shared/store';

type CollateralAssetsSectionProps = {
    assets: Token[];
    disabled?: boolean;
    agreement: Agreement;
    classes?: Partial<Record<'root', string>>;
    assetAmount?: string;
    assetSymbol?: string;
};

export enum AssetSectionFields {
    SELECTED_ASSETS = 'selectedAssets',
}

const MAX_ASSETS = 10;

const sortAssetListByPrice = (assetList: Token[], prices: UserConversionRates[]) =>
    assetList
        .map((asset) => {
            const foundPrice = prices.find((price) => price.address === asset.address)?.price;
            return {
                ...asset,
                price: foundPrice ? String(foundPrice) : '1',
            };
        })
        .sort(
            (a, b) =>
                parseFloat(b.price) * parseFloat(b.balance!) -
                parseFloat(a.price) * parseFloat(a.balance!),
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(({ price, ...rest }) => rest);

export const AssetSection: FC<CollateralAssetsSectionProps> = ({
    assets,
    agreement,
    classes,
    disabled,
    assetAmount,
    assetSymbol,
}) => {
    const baseClasses = useAssetSectionStyle();
    const isInitialized = useRef(false);

    const prices = useSelector((state: Store) => state.borrows.userConversionRates);
    const activeWalletAddress = useSelector((state: Store) => state.wallets.active.address);

    const {
        control,
        trigger,
        setValue,
        formState: { errors },
    } = useFormContext<BorrowModalData>();

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: AssetSectionFields.SELECTED_ASSETS,
    });

    const assetsWithBalance = useMemo(
        () => assets.filter((asset) => Number(asset.balance) !== 0),
        [assets],
    );

    const sortedAssetList = useMemo(
        () => sortAssetListByPrice(assetsWithBalance, prices),
        [assetsWithBalance, prices],
    );

    const assetList = useMemo(
        () =>
            fields.length
                ? sortedAssetList.filter(
                      (asset) => !fields.some((selected) => selected.symbol === asset.symbol),
                  )
                : sortedAssetList,
        [sortedAssetList, fields],
    );

    const createSelectedBorrowAsset = useCallback(
        (asset: Token): SelectedBorrowAsset => ({
            symbol: asset.symbol,
            name: asset.name,
            address: asset.address ?? '',
            amount: asset.balance ?? '0',
            amountInAssetToBorrow: '0',
            decimals: asset.decimals,
            maxBalance: asset.balance as string,
        }),
        [],
    );

    useEffect(() => {
        if (activeWalletAddress) {
            setValue(AssetSectionFields.SELECTED_ASSETS, []);
            isInitialized.current = false;
        }
    }, [activeWalletAddress]);

    useEffect(() => {
        if (isInitialized.current || !assetSymbol) return;

        const defaultAsset = sortedAssetList[0];

        if (defaultAsset || assetsWithBalance?.length) {
            append(createSelectedBorrowAsset(defaultAsset || assetsWithBalance[0]));
            isInitialized.current = true;
        }
    }, [append, createSelectedBorrowAsset, assetSymbol, sortedAssetList, trigger]);

    const addNewAsset = useCallback(async () => {
        const availableAsset = assetList[0];
        if (!availableAsset) {
            console.error('No available asset to add.');
            return;
        }
        setValue(RiskFactorFields.STRATEGY, LeverageStrategy.MANUAL);
        append(createSelectedBorrowAsset(availableAsset));
        await trigger(AssetSectionFields.SELECTED_ASSETS);
    }, [append, assetList, createSelectedBorrowAsset, trigger]);

    const handleDelete = useCallback(
        async (index: number) => {
            setValue(RiskFactorFields.STRATEGY, LeverageStrategy.MANUAL);
            remove(index);
            await trigger(AssetSectionFields.SELECTED_ASSETS);
        },
        [remove, trigger],
    );

    const handleUpdate = useCallback(
        (index: number, asset: Partial<SelectedBorrowAsset>) => {
            setValue(RiskFactorFields.STRATEGY, LeverageStrategy.MANUAL);
            update(index, { ...fields[index], ...asset });
        },
        [update, fields],
    );

    const error = errors[AssetSectionFields.SELECTED_ASSETS]?.message;

    return (
        <div className={classes?.root}>
            <div className={baseClasses?.header}>
                <div>Amount</div>
                <div>Protocol Balance</div>
            </div>

            {fields.map((selectedAsset, index) => (
                <span className={baseClasses.card} key={selectedAsset?.name || index}>
                    <AssetCard
                        index={index}
                        asset={selectedAsset as SelectedBorrowAsset}
                        assetList={assetList}
                        maxBalance={
                            assetsWithBalance.find((item) => item.symbol === selectedAsset.symbol)
                                ?.balance
                        }
                        selectedAssets={fields as SelectedBorrowAsset[]}
                        assetAmount={assetAmount}
                        disabled={disabled}
                        onUpdate={(asset: Partial<SelectedBorrowAsset>) =>
                            handleUpdate(index, asset)
                        }
                        onDelete={() => handleDelete(index)}
                        decimals={selectedAsset.decimals}
                        agreement={agreement}
                        assetSymbol={assetSymbol}
                    />
                </span>
            ))}

            {fields.length < Math.min(assetsWithBalance.length, MAX_ASSETS) && (
                <ButtonStroke
                    isOutline={false}
                    className={baseClasses.button}
                    onClick={addNewAsset}
                >
                    <PlusCircleIcon />
                    Add asset
                </ButtonStroke>
            )}

            {error && <div className={baseClasses.error}>{error}</div>}
        </div>
    );
};
